import { useEffect, useState } from "react";
import { prettifyApiError } from "@/shared/api";
import { ErrorBanner } from "@/shared/ui/ErrorBanner";
import {
  fetchGiorni,
  giornoLungo,
  giornoPredefinitoReso,
  oggiIso,
  prenotaRitiroReso,
  type Prenotazione,
} from "@/features/ritiri/api";
import { StrisciaGiorni } from "@/features/ritiri/StrisciaGiorni";
import { aggiornaReso, type ReturnRecord } from "../api";
import { AVVISO_ESITO_IGNOTO } from "./RitiroReso";

type Props = {
  record: ReturnRecord;
  /** Il ritiro a cui il corriere non è passato: si chiude e se ne fa un altro
   *  sulla stessa etichetta che il cliente ha già. */
  mancato?: Prenotazione | null;
  onChiudi: () => void;
  onPrenotato: () => void;
};

/** Dalla pagina Resi: prenota il ritiro di un reso che non l'ha, o l'ha perso. */
export function PrenotaRitiroResoDialog({ record, mancato, onChiudi, onPrenotato }: Props) {
  const [giorni, setGiorni] = useState<string[]>([]);
  const [giorno, setGiorno] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [errore, setErrore] = useState<string | null>(null);
  // Esito ignoto: il ritiro può esserci. Da qui niente secondo tentativo, e
  // alla chiusura la lista si ricarica e lo mostra «da verificare».
  const [ignoto, setIgnoto] = useState(false);
  // Il ritiro SDA non parte senza il telefono del cliente (mittente del reso):
  // se manca lo si chiede qui e si salva sul reso prima di prenotare.
  const serveTelefono = !(record.sender.phone ?? "").trim();
  const [telefono, setTelefono] = useState("");

  useEffect(() => {
    fetchGiorni()
      .then((risposta) => {
        const g = risposta.giorni ?? [];
        setGiorni(g);
        setGiorno(giornoPredefinitoReso(g, oggiIso()));
      })
      .catch((e: unknown) => setErrore(prettifyApiError(e)));
  }, []);

  async function prenota() {
    if (!giorno) return;
    setBusy(true);
    setErrore(null);
    try {
      if (serveTelefono) await aggiornaReso(record.id, { sender_phone: telefono.trim() });
      const p = await prenotaRitiroReso(record.id, giorno, Boolean(mancato));
      if (p.stato === "rifiutato")
        setErrore(`SDA ha rifiutato: ${p.errore ?? "motivo non indicato"}`);
      else if (p.stato === "esito_ignoto") setIgnoto(true);
      else onPrenotato();
    } catch (e) {
      setErrore(prettifyApiError(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="ritiro-reso-titolo"
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="w-full max-w-lg rounded-lg border border-line bg-card shadow-popover">
        <header className="border-b border-line px-5 py-4">
          <h2 id="ritiro-reso-titolo" className="font-display text-[17px] font-bold text-ink">
            Ritiro dal cliente
          </h2>
          <p className="mt-0.5 text-[12px] text-ink">
            Ordine <span className="tabular-nums">{record.order_reference}</span> ·{" "}
            {record.sender.company || "cliente"}
          </p>
        </header>
        <div className="px-5 py-4">
          {ignoto ? (
            <p className="rounded-sm bg-amber-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
              {AVVISO_ESITO_IGNOTO}
            </p>
          ) : (
            <>
              {mancato && (
                <p className="mb-3 rounded-sm bg-amber-soft px-2.5 py-2 text-[12.5px] font-semibold text-ink">
                  Il ritiro di {giornoLungo(mancato.data_ritiro)}
                  {mancato.codice ? ` (codice ${mancato.codice})` : ""} non è andato: lo chiudo e ne
                  prenoto un altro sulla stessa etichetta. Il cliente non deve stampare niente.
                </p>
              )}
              {serveTelefono && (
                <label className="mb-3 block text-[12.5px] font-semibold text-ink">
                  Telefono del cliente
                  <input
                    type="tel"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    disabled={busy}
                    className="mt-1 block w-full rounded-sm border border-line px-2.5 py-1.5 text-[13px] font-normal"
                  />
                </label>
              )}
              <StrisciaGiorni
                etichetta="Giorno del ritiro"
                giorni={giorni.slice(0, 8)}
                oggi={oggiIso()}
                valore={giorno}
                onChange={setGiorno}
                disabled={busy}
              />
            </>
          )}
        </div>
        <footer className="border-t border-line px-5 py-3">
          <ErrorBanner message={errore} className="mb-2" />
          <div className="flex justify-end gap-2">
            <button
              type="button"
              onClick={ignoto ? onPrenotato : onChiudi}
              disabled={busy}
              className="rounded-sm border border-line px-3.5 py-2 text-[13px] font-bold text-ink hover:bg-paper"
            >
              Chiudi
            </button>
            {!ignoto && (
              <button
                type="button"
                onClick={() => void prenota()}
                disabled={busy || !giorno || (serveTelefono && !telefono.trim())}
                aria-busy={busy}
                className="rounded-sm border border-ink bg-ink px-3.5 py-2 text-[13px] font-bold text-on-ink disabled:cursor-not-allowed disabled:border-line-2 disabled:bg-paper-2 disabled:text-ink"
              >
                {busy ? "Prenotazione in corso…" : "Prenota il ritiro"}
              </button>
            )}
          </div>
        </footer>
      </div>
    </div>
  );
}
